import { AsyncPipe, NgClass, UpperCasePipe } from "@angular/common";
import { Component, ElementRef, signal, ViewChild } from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { of, race, Subject, Subscription, timer } from "rxjs";
import { catchError, filter, map, take, takeUntil } from "rxjs/operators";
import {
  fadeText,
  reduceHeight,
  slideInUp,
  slideOutLeft,
  slideOutLeftForChildBox,
} from "src/app/animations/custom.animation";
import { animationDelays } from "src/app/app.constants";
import { LoadingComponent } from "../../../components/loading/loading.component";
import { CheckWinningsFlowService } from "../check-winnings-flow.service";
import { CheckWinningsRepositoryService } from "../check-winnings-repository.service";
import {
  shouldNavigateToSurpriseGift,
  SURPRISE_GIFT_FREE_CREDIT_ANIMATIONS,
} from "../check-winnings-display";
import {
  CHECK_WINNINGS_CLIENT_TIMEOUT_MS,
  CheckWinningsRequestStatus,
  isCheckWinningsRetryableStatus,
  isCheckWinningsTerminalStatus,
  resolveCheckWinningsRequestStatus,
} from "../check-winnings-request-status";

@Component({
  selector: "app-check-winnings-splash",
  templateUrl: "./check-winnings-splash.component.html",
  styleUrls: ["./check-winnings-splash.component.scss"],
  animations: [slideInUp, fadeText, reduceHeight, slideOutLeft, slideOutLeftForChildBox],
  standalone: true,
  imports: [
    IonicModule,
    NgClass,
    LoadingComponent,
    AsyncPipe,
    UpperCasePipe,
    TranslatePipe,
  ],
})
export class CheckWinningsSplashComponent {
  public isPageLoading = true;
  public tickMark = 0;
  public sliderUpAnimation = false;
  public allCardAnimationDone = false;
  public firstCardCheck = false;
  claimableSyndicates$;
  private unsubscribeAll$ = new Subject<void>();
  private statusWatchSubscription: Subscription | null = null;
  cardDetails = { syndicate_group: [] };
  translatedToastMsg: Data;
  selectedLanguage: string = "en";
  public checkTicketStatus: boolean;
  handleRedirectionSignal = signal<boolean>(false);
  @ViewChild("myAudio") audioRef: ElementRef;
  public check_no: number;
  public check_prize: string;
  private winningSummaryData: Data;
  private freeCreditArray = [...SURPRISE_GIFT_FREE_CREDIT_ANIMATIONS];
  public userId: string;
  public showRetryAction = false;
  public timeoutRetryMessage = "We're still checking your winnings. Please try again in a moment.";

  constructor(
    private checkWinningsService: CheckWinningsRepositoryService,
    private checkWinningsFlowService: CheckWinningsFlowService,
    private router: Router,
    private analytics: AngularFireAnalytics,
    private authService: AuthService,
    public translate: TranslateService
  ) {
    try {
      this.authService?.selectedlanguage$?.subscribe(
        (l) => {
          this.selectedLanguage = l;
          translate.use(l ? l : "en");
        },
        (err) => {
          return err;
        }
      );
      this.authService?.getConstantsMsg$?.subscribe((msg: Data) => {
        this.translatedToastMsg = msg?.static_messages_pwa;
      });
    } catch (e) { }
  }

  async ngOnInit() {
    await this.authService
      ?.getUid("CheckWinningsSplashComponent")
      ?.then((uid) => {
        this.userId = uid;
        this.analytics.logEvent("check_winnings_splash_screen", {
          userId: this.userId,
        });
      })
      .catch((error) => {
        return error;
      });

    this.startCheckWinningsFlow();
  }

  private startCheckWinningsFlow(): void {
    if (!window.navigator.onLine) {
      this.handleTechnicalError();
      return;
    }

    // Single-flight: do not start another backend check on re-entry.
    // Resume Firestore watch when a claim is already in progress; clear a stale lock otherwise.
    if (this.checkWinningsFlowService.isInFlight()) {
      this.resumeInFlightCheck();
      return;
    }

    this.beginFreshCheckWinningsFlow();
  }

  private resumeInFlightCheck(): void {
    const claimEventId = this.checkWinningsFlowService.getActiveClaimEventId();
    if (!claimEventId) {
      this.checkWinningsFlowService.completeCheck({
        userId: this.userId,
        outcomeStatus: "cancelled",
        memberFacingMessage: "stale_inflight_cleared",
      });
      this.beginFreshCheckWinningsFlow();
      return;
    }

    this.check_no = this.checkWinningsFlowService.getActiveCheckNo() as number;
    this.check_prize = this.checkWinningsFlowService.getActiveCheckPrize();

    const apiData: Data = {
      response: {
        claim_event_id: claimEventId,
        check_no: this.check_no,
        check_prize: this.check_prize,
      },
    };
    this.cardDetails["claim_event_id"] = claimEventId;
    this.claimableSyndicates$ = of(apiData);
    this.watchForResolvedStatus(claimEventId, apiData);
  }

  private beginFreshCheckWinningsFlow(): void {
    this.checkWinningsFlowService.startCheck({ userId: this.userId, source: "splash" });

    // isWinning() only populates card animation details — never block the claim request on it.
    this.checkWinningsService
      .isWinning()
      .pipe(
        take(1),
        takeUntil(this.unsubscribeAll$),
        catchError(() => of(null))
      )
      .subscribe((data) => {
        if (data?.response?.status?.toLowerCase() === "success") {
          this.cardDetails.syndicate_group =
            data?.response?.claimable_syndicate_details ?? [];
        }
        this.fetchInitialCheckWinningsResponse();
      });
  }

  private fetchInitialCheckWinningsResponse(): void {
    this.checkWinningsService
      .initiateCheckWinningsRequest()
      .pipe(
        take(1),
        takeUntil(this.unsubscribeAll$),
        catchError(() => of({ error: true }))
      )
      .subscribe((data: Data) => {
        if (!data || data?.error) {
          this.handleTechnicalError();
          return;
        }
        this.handleInitialApiResponse(data);
      });
  }

  private handleInitialApiResponse(data: Data): void {
    this.check_no = data?.response?.check_no;
    this.check_prize = data?.response?.check_prize;

    if (data?.response?.status?.toLowerCase() === "fail") {
      this.handleTechnicalError();
      return;
    }

    const claimEventId = data?.response?.claim_event_id;
    if (!claimEventId) {
      this.handleTechnicalError();
      return;
    }

    this.cardDetails["claim_event_id"] = claimEventId;
    this.checkWinningsFlowService.trackRequestStart(
      claimEventId,
      this.userId,
      this.check_no,
      this.check_prize
    );
    this.claimableSyndicates$ = of(data);

    const immediateStatus = resolveCheckWinningsRequestStatus(null, data);
    if (immediateStatus !== "unknown") {
      this.handleResolvedStatus(immediateStatus, data);
      return;
    }

    this.watchForResolvedStatus(claimEventId, data);
  }

  private watchForResolvedStatus(claimEventId: string, apiData: Data): void {
    this.statusWatchSubscription?.unsubscribe();

    const status$ = this.checkWinningsService.watchCheckWinningsDocument(claimEventId).pipe(
      map((doc) => ({
        doc,
        status: resolveCheckWinningsRequestStatus(doc, apiData),
      })),
      // Only terminal outcomes settle the race — pending/processing must keep waiting
      // so the client timeout can still fire and avoid a hung splash.
      filter(
        (result): result is { doc: Data | null; status: CheckWinningsRequestStatus } =>
          result.status !== "unknown" && isCheckWinningsTerminalStatus(result.status)
      ),
      take(1)
    );

    const timeout$ = timer(CHECK_WINNINGS_CLIENT_TIMEOUT_MS).pipe(
      map(() => ({
        doc: null as Data | null,
        status: CheckWinningsRequestStatus.TimedOut,
      }))
    );

    this.statusWatchSubscription = race(status$, timeout$)
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe({
        next: ({ doc, status }) => {
          if (doc) {
            this.winningSummaryData = doc;
          }
          this.handleResolvedStatus(status, apiData);
        },
        // Keep the active claim so retry can re-watch instead of starting a duplicate request.
        error: () => this.handleStatusWatchError(claimEventId, apiData),
      });
  }

  private handleResolvedStatus(status: CheckWinningsRequestStatus, apiData: Data): void {
    switch (status) {
      case CheckWinningsRequestStatus.CompletedWithWinnings:
        this.showRetryAction = false;
        this.checkWinningsFlowService.completeCheck({
          userId: this.userId,
          claimEventId: apiData?.response?.claim_event_id,
          outcomeStatus: status,
          memberFacingMessage: "checking_complete_with_winnings",
        });
        this.delayAnimation();
        return;

      case CheckWinningsRequestStatus.CompletedEmpty:
        this.showRetryAction = false;
        this.checkWinningsFlowService.completeCheck({
          userId: this.userId,
          claimEventId: apiData?.response?.claim_event_id,
          outcomeStatus: status,
          memberFacingMessage: "no_winnings_found",
        });
        this.handleEmptyResult();
        return;

      case CheckWinningsRequestStatus.Failed:
        this.showRetryAction = isCheckWinningsRetryableStatus(status);
        this.checkWinningsFlowService.completeCheck({
          userId: this.userId,
          claimEventId: apiData?.response?.claim_event_id,
          outcomeStatus: status,
          memberFacingMessage: "technical_error_retryable",
        });
        this.handleTechnicalError(true);
        return;

      case CheckWinningsRequestStatus.TimedOut:
        // Do not completeCheck — backend may still finish; retry resumes the same claim watch.
        this.showRetryAction = true;
        this.handleTimeoutError();
        return;

      case CheckWinningsRequestStatus.Pending:
      case CheckWinningsRequestStatus.Processing:
      default:
        return;
    }
  }

  private handleEmptyResult(): void {
    this.router.navigateByUrl("mainTabs/tabs/syndicate");
    this.authService
      .getConstantsMsg("1038")
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "no winning found",
            line_2: "",
          },
          style: {
            icon: "tick circle alert.svg",
            type: "warning",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((err) => err);
  }

  private handleTechnicalError(retryable = false, claimEventId?: string): void {
    if (!retryable) {
      this.checkWinningsFlowService.completeCheck({
        userId: this.userId,
        claimEventId:
          claimEventId ?? this.checkWinningsFlowService.getActiveClaimEventId() ?? undefined,
        outcomeStatus: "technical_error",
        memberFacingMessage: "technical_error",
      });
      this.router.navigateByUrl("mainTabs/tabs/syndicate");
    } else {
      this.showRetryAction = true;
    }

    this.authService
      .getConstantsMsg("1014")
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: retryable
              ? "We could not finish checking your winnings. Please try again."
              : "Something went wrong",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((err) => err);
  }

  /**
   * Firestore status-watch failure: keep in-flight claim so retry re-watches the same event.
   */
  private handleStatusWatchError(claimEventId: string, apiData: Data): void {
    if (!claimEventId) {
      this.handleTechnicalError(
        false,
        apiData?.response?.claim_event_id
      );
      return;
    }
    this.showRetryAction = true;
    this.authService
      .getConstantsMsg("1014")
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "We could not finish checking your winnings. Please try again.",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((err) => err);
  }

  /**
   * Client/backend wait window elapsed — retryable timeout via Firebase msg 1177,
   * not empty winnings and not generic 1014 "Something went wrong".
   */
  private handleTimeoutError(): void {
    this.showRetryAction = true;
    this.authService
      .getConstantsMsg("1177")
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "We're still checking your winnings. Please try again in a moment.",
            line_2: "",
          },
          style: {
            icon: "tick circle alert.svg",
            type: "warning",
          },
        };
        this.timeoutRetryMessage =
          translatedMsg?.message?.line_1 || defaultMsg.message.line_1;
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((err) => err);
  }

  retryCheckWinnings(): void {
    this.showRetryAction = false;
    this.checkWinningsFlowService.registerRetry();

    // Prefer resuming the existing claim watch (timeout / watch error) over a new backend request.
    const activeClaimEventId = this.checkWinningsFlowService.getActiveClaimEventId();
    if (activeClaimEventId && this.checkWinningsFlowService.isInFlight()) {
      this.resumeInFlightCheck();
      return;
    }

    this.checkWinningsFlowService.startCheck({ userId: this.userId, source: "splash_retry" });
    this.fetchInitialCheckWinningsResponse();
  }

  goBackToSyndicate(): void {
    this.checkWinningsFlowService.completeCheck({
      userId: this.userId,
      outcomeStatus: "cancelled",
      memberFacingMessage: "user_cancelled_retry",
    });
    this.router.navigateByUrl("mainTabs/tabs/syndicate");
  }

  delayAnimation() {
    try {
      timer(10)
        .pipe(takeUntil(this.unsubscribeAll$))
        ?.subscribe(
          () => {
            this.beginAnimation();
          },
          (err) => {
            return err;
          }
        );
    } catch (e) { }
  }

  removeTheCardOneByOne() {
    this.firstCardCheck = true;
    this.tickMark = 0;

    if (this.allCardAnimationDone) {
      return;
    }
    try {
      timer(animationDelays.tickMarkDelay)
        .pipe(takeUntil(this.unsubscribeAll$))
        ?.subscribe(
          () => {
            if (this.cardDetails?.syndicate_group?.length > 0) {
              this.cardDetails.syndicate_group[0].removedSubTitleCard = true;
            }
          },
          (err) => {
            return err;
          }
        );

      timer(animationDelays.bottomTextVisibilityDelay)
        .pipe(takeUntil(this.unsubscribeAll$))
        ?.subscribe(
          () => {
            if (this.cardDetails?.syndicate_group.length > 0) {
              this.cardDetails.syndicate_group[0].bottomTextVisible = true;
            }
          },
          (err) => {
            return err;
          }
        );
    } catch (e) { }
    try {
      timer(animationDelays.removeEntireCardDelay)
        .pipe(takeUntil(this.unsubscribeAll$))
        ?.subscribe(
          () => {
            if (this.cardDetails?.syndicate_group.length > 0) {
              this.cardDetails.syndicate_group[0].removeEntireCard = true;
            }
          },
          (err) => {
            return err;
          }
        );

      timer(animationDelays.reduceCardHeightDelay)
        .pipe(takeUntil(this.unsubscribeAll$))
        ?.subscribe(
          () => {
            if (this.cardDetails?.syndicate_group.length > 0) {
              this.cardDetails.syndicate_group[0].reduceHeight = true;
            }
          },
          (err) => {
            return err;
          }
        );
    } catch (e) { }
    try {
      timer(animationDelays.shiftArrayDelay)
        .pipe(takeUntil(this.unsubscribeAll$))
        ?.subscribe(() => {
          for (let cardIndex = 0; cardIndex < this.cardDetails?.syndicate_group?.length; cardIndex++) {
            this.tickMark = 1;
            if (this.cardDetails?.syndicate_group[0].reduceHeight) this.cardDetails?.syndicate_group.shift();
            break;
          }

          if (this.cardDetails?.syndicate_group?.length) {
            timer(animationDelays.loopingSpace)
              .pipe(takeUntil(this.unsubscribeAll$))
              ?.subscribe(
                () => {
                  this.removeTheCardOneByOne();
                },
                (err) => {
                  return err;
                }
              );
          } else {
            this.allCardAnimationDone = true;
            timer(2000)
              .pipe(takeUntil(this.unsubscribeAll$))
              ?.subscribe(
                () => {
                  this.movetoPage();
                },
                (err) => {
                  return err;
                }
              );
          }
        });
    } catch (e) { }
  }

  ngOnDestroy() {
    this.statusWatchSubscription?.unsubscribe();
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  beginAnimation() {
    try {
      timer(animationDelays.beginAnimation)
        .pipe(takeUntil(this.unsubscribeAll$))
        ?.subscribe(
          () => {
            this.sliderUpAnimation = true;
          },
          (err) => {
            return err;
          }
        );

      timer(500)
        .pipe(takeUntil(this.unsubscribeAll$))
        ?.subscribe(
          () => {
            this.tickMark = 1;
          },
          (err) => {
            return err;
          }
        );

      timer(animationDelays.animationStartDelay)
        .pipe(takeUntil(this.unsubscribeAll$))
        ?.subscribe(
          () => {
            this.removeTheCardOneByOne();
          },
          (err) => {
            return err;
          }
        );
    } catch (e) { }
  }

  private hasQueryValue(value: unknown): boolean {
    if (value == null) {
      return false;
    }
    const asString = String(value).trim().toLowerCase();
    return asString !== "" && asString !== "null" && asString !== "undefined";
  }

  private buildCheckWinningsQuery(): string {
    const params = new URLSearchParams();
    if (this.hasQueryValue(this.check_no)) {
      params.set("check_no", String(this.check_no));
    }
    if (this.hasQueryValue(this.check_prize)) {
      params.set("check_prize", String(this.check_prize));
    }
    const query = params.toString();
    return query ? `?${query}` : "";
  }

  movetoPage() {
    const query = this.buildCheckWinningsQuery();
    const sanitizedUrl = `checkWinnings${query}`;
    const claimEventId = this.cardDetails["claim_event_id"];

    const navigateWithoutSummary = (): void => {
      if (this.handleRedirectionSignal()) {
        return;
      }
      this.handleRedirectionSignal.set(true);
      this.router.navigateByUrl(sanitizedUrl);
    };

    // Prefer claim-scoped doc captured during status watch; otherwise read this claim once
    // (avoid shareReplay take(1) which can replay a prior claim's gift animation).
    if (this.winningSummaryData) {
      this.checkWinningsService?.updateClaimEventId(claimEventId);
      this.redirectAfterSplash(this.winningSummaryData, sanitizedUrl, query);
      return;
    }

    // Missing/invalid id: watchCheckWinningsDocument("") emits null then completes —
    // filter(!!doc) would never emit and leave the splash hung with no navigation.
    if (!claimEventId) {
      navigateWithoutSummary();
      return;
    }

    this.checkWinningsService?.updateClaimEventId(claimEventId);

    const summary$ = this.checkWinningsService.watchCheckWinningsDocument(claimEventId).pipe(
      filter((doc): doc is Data => !!doc),
      take(1)
    );

    // Doc may never exist / stay null forever on onSnapshot — always fall back to summary route.
    const fallback$ = timer(5_000).pipe(map(() => null as Data | null));

    race(summary$, fallback$)
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe({
        next: (data) => {
          if (data) {
            this.winningSummaryData = data;
            this.redirectAfterSplash(data, sanitizedUrl, query);
            return;
          }
          navigateWithoutSummary();
        },
        error: () => navigateWithoutSummary(),
      });
  }

  private redirectAfterSplash(summary: Data, sanitizedUrl: string, query: string): void {
    if (this.handleRedirectionSignal()) {
      return;
    }
    this.handleRedirectionSignal.set(true);

    if (shouldNavigateToSurpriseGift(summary, this.freeCreditArray)) {
      const currentAnimation = summary?.gift_won_animation;
      this.checkWinningsService.surpriseAnimationDocument = currentAnimation;
      this.router.navigateByUrl(`/checkWinnings/surprise-gift${query}`);
      return;
    }

    this.router.navigateByUrl(sanitizedUrl);
  }

  public playAudio() {
    const audio: HTMLAudioElement = this.audioRef?.nativeElement;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          return;
        })
        .catch((error) => {
          return error;
        });
    }
  }

  public ticketStatus() {
    this.checkTicketStatus = true;
    this.playAudio();
    setTimeout(() => {
      this.checkTicketStatus = false;
    }, 1000);
  }
}
